import React, { useState, createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { User, Mail, Lock, UserPlus, LogIn, Shield, Crown, Eye, EyeOff, BarChart3, Settings, Scan } from 'lucide-react';
import { AdminPanel } from './AdminPanel';
import { UserProfileModal } from './UserProfileModal';
import api from '../services/api';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      // Check if this is admin credentials (case-insensitive email)
      const isAdmin = email.toLowerCase() === 'dan@alich.com' && password === 'Password25';
      
      if (isAdmin) {
        // Use admin login API
        const result = await api.adminLogin({ email, password });
        if (result.success) {
          const adminUser = {
            ...result.admin,
            subscription: 'enterprise' // Admins get enterprise features
          };
          setUser(adminUser);
          localStorage.setItem('trackme_user', JSON.stringify(adminUser));
          localStorage.setItem('adminToken', result.admin.token);
          localStorage.setItem('adminUser', JSON.stringify(result.admin));
          return { success: true, isAdmin: true };
        }
      } else {
        // Try regular user login
        try {
          const result = await api.userLogin(email, password);
          if (result.success) {
            setUser(result.user);
            localStorage.setItem('trackme_user', JSON.stringify(result.user));
            return { success: true, isAdmin: false };
          }
        } catch (loginError) {
          // Check if this is a "no password" error (403)
          if (loginError.response?.status === 403 || loginError.message?.includes('no_password')) {
            return { 
              success: false, 
              needsPasswordSetup: true,
              email: email,
              error: 'Your account exists but has no password set. Please use password reset or sign in with a magic link.' 
            };
          }
          // If login fails, return error
          return { success: false, error: 'Invalid credentials' };
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Invalid credentials' };
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('trackme_user', JSON.stringify(updatedUser));
  };

  const sendMagicLink = async (email) => {
    setIsLoading(true);
    try {
      const result = await api.sendMagicLink(email);
      // Backend always returns success: true (even if email doesn't exist for security)
      return { success: true, message: result.message || 'If an account exists, a magic link has been sent to your email.' };
    } catch (error) {
      console.error('Send magic link error:', error);
      // Show generic success message for security (don't reveal if email exists)
      return { success: true, message: 'If an account exists, a magic link has been sent to your email.' };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyMagicLink = async (token) => {
    setIsLoading(true);
    try {
      const result = await api.verifyMagicLink(token);
      if (result.success) {
        setUser(result.user);
        localStorage.setItem('trackme_user', JSON.stringify(result.user));
        return { success: true, user: result.user };
      }
      return { success: false, error: 'Invalid or expired magic link' };
    } catch (error) {
      console.error('Verify magic link error:', error);
      return { success: false, error: 'Magic link verification failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name, email, password) => {
    setIsLoading(true);
    try {
      const result = await api.registerUser(name, email, password);
      if (result.success) {
        setUser(result.user);
        localStorage.setItem('trackme_user', JSON.stringify(result.user));
        return { success: true };
      } else {
        return { success: false, error: result.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.status === 400) {
        return { success: false, error: 'User already exists' };
      }
      return { success: false, error: 'Registration failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('trackme_user');
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const savedUser = localStorage.getItem('trackme_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, isLoading, sendMagicLink, verifyMagicLink }}>
      {children}
    </AuthContext.Provider>
  );
};

export const ForgotPasswordForm = ({ onBack, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    try {
      const result = await api.forgotPassword(email);
      if (result.success) {
        setIsSuccess(true);
        setMessage('If the email exists, a reset link has been sent to your inbox.');
      } else {
        setMessage(result.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-6 h-6" />
            Reset Password
          </CardTitle>
        </CardHeader>

        <CardContent>
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="text-emerald-600 text-4xl mb-4">✓</div>
              <h3 className="text-lg font-medium mb-2">Check Your Email</h3>
              <p className="text-gray-600 text-sm mb-4">
                If an account with that email exists, we've sent you a password reset link.
              </p>
            </div>
          )}

          {message && (
            <div className={`mt-4 p-3 rounded-md text-sm ${
              isSuccess 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <Button variant="ghost" onClick={onBack} className="flex-1">
              Back to Login
            </Button>
            <Button variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const LoginForm = ({ onClose }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkMessage, setMagicLinkMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const { login, register, isLoading, sendMagicLink } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(''); // Clear previous errors
    
    let result;
    if (isLogin) {
      result = await login(formData.email, formData.password);
      
      // Check if account needs password setup
      if (result.needsPasswordSetup) {
        setErrorMessage(result.error);
        // Show both password reset and magic link options
        return;
      }
      
      // Handle "Remember me" functionality
      if (result.success && rememberMe) {
        localStorage.setItem('trackme_remember', 'true');
        localStorage.setItem('trackme_email', formData.email);
      }
    } else {
      result = await register(formData.name, formData.email, formData.password);
    }
    
    if (result.success) {
      onClose();
      // Redirect admins to dashboard, preserving deep-link query string
      if (result.isAdmin) {
        const search = window.location.search || '';
        navigate(`/admin/dashboard${search}`);
      }
    } else {
      setErrorMessage(result.error || 'An error occurred. Please try again.');
    }
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setMagicLinkMessage('Please enter your email address');
      return;
    }
    
    const result = await sendMagicLink(formData.email);
    if (result.success) {
      setMagicLinkSent(true);
      setMagicLinkMessage(result.message || 'Magic link sent! Check your email.');
    } else {
      setMagicLinkMessage(result.error || 'Failed to send magic link');
    }
  };

  // Pre-fill email if remembered
  React.useEffect(() => {
    const remembered = localStorage.getItem('trackme_remember');
    const savedEmail = localStorage.getItem('trackme_email');
    if (remembered && savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} onClose={onClose} />;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isLogin ? <LogIn className="w-6 h-6" /> : <UserPlus className="w-6 h-6" />}
              {isLogin ? 'Sign In' : 'Create Account'}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              ×
            </Button>
          </CardTitle>
        </CardHeader>

        <CardContent className="px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Name
                </label>
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter your name"
                  className="h-12 text-base"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email
              </label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Enter your email"
                className="h-12 text-base"
              />
            </div>

            {/* Magic Link Section */}
            {isLogin && !showMagicLink && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">or</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMagicLink(true)}
                  className="text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                >
                  Sign in with Magic Link
                </Button>
              </div>
            )}

            {isLogin && showMagicLink && !magicLinkSent && (
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-sm font-medium text-purple-800 mb-2">Magic Link Sign In</h3>
                <p className="text-xs text-purple-600 mb-3">
                  We'll send you a secure link to sign in without a password.
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleMagicLinkSubmit}
                    disabled={isLoading}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    size="sm"
                  >
                    {isLoading ? 'Sending...' : 'Send Magic Link'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowMagicLink(false)}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
                {magicLinkMessage && (
                  <div className="mt-2 p-2 text-xs bg-white rounded border">
                    {magicLinkMessage}
                  </div>
                )}
              </div>
            )}

            {isLogin && magicLinkSent && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <div className="text-green-600 text-2xl mb-2">📧</div>
                <h3 className="text-sm font-medium text-green-800 mb-1">Check Your Email</h3>
                <p className="text-xs text-green-600 mb-3">{magicLinkMessage}</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowMagicLink(false);
                    setMagicLinkSent(false);
                    setMagicLinkMessage('');
                  }}
                  size="sm"
                  className="text-green-600 border-green-300 hover:bg-green-100"
                >
                  Back to Sign In
                </Button>
              </div>
            )}

            {/* Only show password field if not using magic link */}
            {(!isLogin || !showMagicLink) && (
              <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">
                  <Lock className="w-4 h-4 inline mr-2" />
                  Password
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-emerald-600 hover:text-emerald-700"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter your password"
                  className="w-full h-12 px-4 pr-14 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-emerald-600 transition-colors focus:outline-none focus:text-emerald-600 z-10"
                  title={showPassword ? "Hide password" : "Show password"}
                  style={{ background: 'none', border: 'none' }}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>
            )}

            {isLogin && (
              <div className="flex items-center justify-between py-2">
                <label className="flex items-center text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="mr-2 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Remember me
                </label>
              </div>
            )}

            {/* Error Message Display */}
            {errorMessage && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                <p className="text-sm text-yellow-800 text-center mb-3">{errorMessage}</p>
                
                {/* Show recovery options if account exists but no password */}
                {errorMessage.includes('no password') && (
                  <div className="space-y-2 mt-3">
                    <p className="text-xs text-yellow-700 text-center font-medium">Choose a sign-in method:</p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-yellow-400 text-yellow-800 hover:bg-yellow-100"
                      >
                        Set Password
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowMagicLink(true);
                          setErrorMessage('');
                        }}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-purple-400 text-purple-800 hover:bg-purple-100"
                      >
                        Use Magic Link
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-medium mt-6"
              disabled={isLoading}
            >
              {isLoading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>

          <div className="mt-4 text-center">
            <Button variant="ghost" onClick={onClose} className="text-gray-500">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const PasswordResetForm = ({ token, onClose }) => {
  const [passwords, setPasswords] = useState({
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    // Validate passwords
    if (passwords.new !== passwords.confirm) {
      setMessage('Passwords do not match.');
      setIsLoading(false);
      return;
    }
    
    if (passwords.new.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      setIsLoading(false);
      return;
    }
    
    try {
      const result = await api.resetPassword(token, passwords.new);
      if (result.success) {
        setIsSuccess(true);
        setMessage('Password has been reset successfully! You can now log in with your new password.');
      } else {
        setMessage(result.message || 'Failed to reset password. The link may have expired.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setMessage('An error occurred. The reset link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full mx-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-6 h-6" />
            Reset Your Password
          </CardTitle>
        </CardHeader>

        <CardContent>
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? "text" : "password"}
                    required
                    value={passwords.new}
                    onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                    placeholder="Enter new password"
                    className="h-12 text-base pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? "text" : "password"}
                    required
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                    placeholder="Confirm new password"
                    className="h-12 text-base pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="text-emerald-600 text-4xl mb-4">✓</div>
              <h3 className="text-lg font-medium mb-2">Password Reset Complete</h3>
              <p className="text-gray-600 text-sm mb-4">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700">
                Go to Login
              </Button>
            </div>
          )}

          {message && (
            <div className={`mt-4 p-3 rounded-md text-sm ${
              isSuccess 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          {!isSuccess && (
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const ChangePasswordForm = ({ user, onBack, onClose }) => {
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    // Validate passwords
    if (passwords.new !== passwords.confirm) {
      setMessage('New passwords do not match.');
      setIsLoading(false);
      return;
    }
    
    if (passwords.new.length < 6) {
      setMessage('New password must be at least 6 characters long.');
      setIsLoading(false);
      return;
    }
    
    try {
      const result = await api.changePassword(passwords.current, passwords.new, user.email);
      if (result.success) {
        setIsSuccess(true);
        setMessage('Password changed successfully!');
        setTimeout(() => {
          onBack();
        }, 2000);
      } else {
        setMessage(result.message || 'Failed to change password.');
      }
    } catch (error) {
      console.error('Change password error:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-6 h-6" />
            Change Password
          </CardTitle>
        </CardHeader>

        <CardContent>
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Current Password
                </label>
                <Input
                  type="password"
                  required
                  value={passwords.current}
                  onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  New Password
                </label>
                <Input
                  type="password"
                  required
                  value={passwords.new}
                  onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  required
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  placeholder="Confirm new password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={isLoading}
              >
                {isLoading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="text-emerald-600 text-4xl mb-4">✓</div>
              <h3 className="text-lg font-medium mb-2">Password Changed</h3>
              <p className="text-gray-600 text-sm">
                Your password has been successfully changed.
              </p>
            </div>
          )}

          {message && (
            <div className={`mt-4 p-3 rounded-md text-sm ${
              isSuccess 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <Button variant="ghost" onClick={onBack} className="flex-1">
              Back to Profile
            </Button>
            <Button variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const UserProfile = ({ user, onClose }) => {
  const { logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [userStats, setUserStats] = useState({ itemsCreated: 0, storiesAdded: 0, totalScans: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  // Safety check for user object
  if (!user) {
    return null;
  }

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Fetch user statistics
  useEffect(() => {
    const fetchUserStats = async () => {
      if (user?.email) {
        try {
          setLoadingStats(true);
          const statsResponse = await api.getUserStats(user.email);
          if (statsResponse.success && statsResponse.stats) {
            setUserStats(statsResponse.stats);
          }
        } catch (error) {
          console.error('Error fetching user stats:', error);
          // Keep default stats if error occurs
        } finally {
          setLoadingStats(false);
        }
      }
    };

    fetchUserStats();
  }, [user?.email]);

  // Handle click outside to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  const handleAdminPanel = () => {
    onClose();
    navigate('/admin/dashboard');
  };

  const handleSaveProfile = async (formData) => {
    setIsUpdating(true);
    try {
      const result = await api.updateUserProfile(formData);
      if (result.success) {
        // Update the user data in context
        const updatedUser = { ...user, ...formData };
        setUser(updatedUser);
        localStorage.setItem('trackme_user', JSON.stringify(updatedUser));
        setShowEditProfile(false);
      } else {
        alert('Failed to update profile: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const userInitials = user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
  const userName = user.name || 'User';
  const userEmail = user.email || 'No email';
  const userRole = user.role || 'user';

  if (showChangePassword) {
    return <ChangePasswordForm user={user} onBack={() => setShowChangePassword(false)} onClose={onClose} />;
  }

  if (showEditProfile) {
    return (
      <UserProfileModal
        user={user}
        onSave={handleSaveProfile}
        onClose={() => setShowEditProfile(false)}
        isLoading={isUpdating}
      />
    );
  }

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <Card className="max-w-lg w-full">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {userRole === 'admin' ? <Crown className="w-6 h-6 text-yellow-600" /> : <User className="w-6 h-6" />}
                User Profile
                {userRole === 'admin' && (
                  <Badge className="bg-yellow-600 text-white">Admin</Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0 hover:bg-gray-100"
              >
                ×
              </Button>
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center mb-2">
                <span className="text-xl font-bold text-white">{userInitials}</span>
              </div>
              <h2 className="text-lg font-bold text-gray-900">{userName}</h2>
              <p className="text-sm text-gray-600">{userEmail}</p>
              <p className="text-xs text-gray-500">Member since {
                user.joinDate || user.createdAt 
                  ? new Date(user.joinDate || user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                  : 'Recently'
              }</p>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-emerald-50 rounded-lg p-2">
                <div className="text-xl font-bold text-emerald-600">
                  {loadingStats ? '...' : userStats.itemsCreated}
                </div>
                <div className="text-xs text-gray-600">Items</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-2">
                <div className="text-xl font-bold text-blue-600">
                  {loadingStats ? '...' : userStats.storiesAdded}
                </div>
                <div className="text-xs text-gray-600">Stories</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-2">
                <div className="text-xl font-bold text-purple-600">
                  {loadingStats ? '...' : userStats.totalScans}
                </div>
                <div className="text-xs text-gray-600">Scans</div>
              </div>
            </div>

            {userRole === 'admin' && (
              <Button 
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                onClick={handleAdminPanel}
              >
                <Shield className="w-4 h-4 mr-2" />
                Open Admin Dashboard
              </Button>
            )}

            <div className="space-y-3">
              <Button 
                variant="outline" 
                onClick={() => setShowEditProfile(true)} 
                className="w-full"
              >
                <User className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => setShowChangePassword(true)} 
                className="w-full"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>

              {/* Batch Story Processing — available to ALL logged-in users.
                  Free tier: up to 20 items per batch, 3 batches/day.
                  Premium / Admin: unlimited (Crown icon shown). */}
              {(() => {
                const isUnlimited = user.role === 'admin' || user.role === 'owner' ||
                  user.subscription === 'premium' ||
                  user.subscriptionTier === 'premium' ||
                  user.subscriptionTier === 'lifetime' ||
                  user.isPremium;
                return (
                  <div
                    className="p-3 border-2 border-yellow-300 rounded-lg bg-yellow-50/60"
                    data-testid="batch-story-processing-card"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm flex items-center">
                        Batch Story Processing
                        {isUnlimited && <Crown className="w-4 h-4 text-yellow-600 ml-2" data-testid="batch-story-unlimited-crown" />}
                      </h4>
                      <Scan className="w-4 h-4 text-yellow-700 mt-0.5" />
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      Register multiple QR codes or add stories to items in one go using your phone&apos;s camera.
                    </p>
                    <p className="text-xs text-gray-500 mb-2" data-testid="batch-story-tier-hint">
                      {isUnlimited
                        ? 'Your plan: Unlimited items per batch.'
                        : 'Free plan: up to 20 items per batch, 3 batches per day.'}
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-yellow-600 text-yellow-700 hover:bg-yellow-100"
                      data-testid="start-batch-story-processing-btn"
                      onClick={() => {
                        navigate('/bulk-register');
                        onClose();
                      }}
                    >
                      <Scan className="w-4 h-4 mr-2" />
                      Start Batch Story Processing
                    </Button>
                  </div>
                );
              })()}

              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Close
                </Button>
                <Button onClick={handleLogout} variant="destructive" className="flex-1">
                  Sign Out
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}
    </>
  );
};